import { useMemo, useState } from "react";

import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

import useMutationRequest from "~/v2/api/hooks/useMutationRequest";
import {
  getStripeCouponCodeValidity,
  type StripeCoupon,
} from "~/v2/api/rest/billing";
import { FullScreenSpinner } from "~/v2/components/common/FullScreenSpinner";
import { getPrettyCash } from "~/v2/utils/formatting";
import type { SubscribeStepProps } from "../Subscribe.controller";
import { useMarketDataReviewController } from "./Review.controller";
import { ReviewPres } from "./Review.pres";

export type CouponProps = {
  discount: string;
  code: string;
  appliedCoupon: StripeCoupon | null;
  error: string | null;
  onChange: (couponCode: string) => void;
  onApply: () => void;
  onRemove: () => void;
  isLoading: boolean;
  isDisabled: boolean;
};

const stripePromise = loadStripe(window.env.STRIPE);

/**
 * Need coupon and Plan price to populate the amount field passed to Stripe context.
 * Stripe/Elements context must wrap ReviewPres, (we are using Stripe Elements in ReviewPres)
 */
export const ReviewConnector = ({
  isElite,
  details,
  plans,
  paymentSchedule,
  ...rest
}: SubscribeStepProps) => {
  const [couponCode, setCouponCode] = useState<string>("");
  const [appliedCoupon, setAppliedCoupon] = useState<StripeCoupon | null>(null);
  const [couponCodeError, setCouponCodeError] = useState<string | null>(null);
  const [couponDiscount, setCouponDiscount] = useState<number>(0); // in Cents

  const couponCodeMutation = useMutationRequest(
    (couponCode: string) => getStripeCouponCodeValidity(couponCode),
    {
      onSuccess: (coupon) => {
        if (coupon.valid) {
          setAppliedCoupon(coupon);
          setCouponCodeError(null);

          const planRawPrice =
            paymentSchedule === "yearly"
              ? plans.yearly.rawPrice
              : plans.monthly.rawPrice;

          if (typeof coupon.amount_off === "number" && coupon.amount_off > 0) {
            // coupon is in cents
            setCouponDiscount(coupon.amount_off);
          } else if (
            typeof coupon.percent_off === "number" &&
            coupon.percent_off > 0
          ) {
            setCouponDiscount((coupon.percent_off / 100) * planRawPrice);
          } else {
            console.error(
              "Coupon is valid but does not contain a number for amount_off or percent_off",
            );
            setCouponDiscount(0);
            setAppliedCoupon(null);
            setCouponCodeError("Invalid coupon code");
          }
        } else {
          setCouponCodeError("Invalid coupon code");
          setAppliedCoupon(null);
          setCouponDiscount(0);
        }
      },
      onError: (error) => {
        console.error("Error applying coupon: ", error);
        setCouponCodeError("Invalid coupon code");
        setAppliedCoupon(null);
        setCouponDiscount(0);
      },
    },
  );

  const onCouponCodeChange = (couponCode: string) => {
    setCouponCode(couponCode);
    setCouponCodeError(null);
  };

  const onApplyCoupon = () => couponCodeMutation.mutate(couponCode);

  const onRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponDiscount(0);
    setCouponCodeError(null);
  };

  const isApplyCouponDisabled =
    couponCodeMutation.isPending ||
    couponCode.trim().length === 0 ||
    !!appliedCoupon;

  const totalPrice = useMemo(() => {
    const isMinimumFundingCompleted = details?.funding_special_enabled;

    // Free
    if (isElite || isMinimumFundingCompleted) {
      return {
        rawPrice: 0,
        displayPrice: getPrettyCash(0, {
          acceptZero: true,
          minimumFractionDigits: 2,
        }),
      };
    }

    const planRawPrice =
      paymentSchedule === "yearly"
        ? plans.yearly.rawPrice
        : plans.monthly.rawPrice;

    return {
      rawPrice: planRawPrice - couponDiscount,
      displayPrice: getPrettyCash((planRawPrice - couponDiscount) / 100, {
        acceptZero: true,
        minimumFractionDigits: 2,
      }),
    };
  }, [
    isElite,
    details?.funding_special_enabled,
    paymentSchedule,
    plans,
    couponDiscount,
  ]);

  const coupon: CouponProps = {
    discount: getPrettyCash(couponDiscount / -100, {
      acceptZero: true,
      minimumFractionDigits: 2,
    }),
    code: couponCode,
    appliedCoupon,
    error: couponCodeError,
    onChange: onCouponCodeChange,
    onApply: onApplyCoupon,
    onRemove: onRemoveCoupon,
    isLoading: couponCodeMutation.isPending,
    isDisabled: isApplyCouponDisabled,
  };

  return (
    <Elements
      stripe={stripePromise}
      options={{
        mode: "subscription",
        // pass amount in US Cents - raw price
        amount: totalPrice.rawPrice,
        currency: "usd",
      }}
    >
      <ReviewConnectorWithStripe
        totalPrice={totalPrice.displayPrice}
        coupon={coupon}
        isElite={isElite}
        details={details}
        plans={plans}
        paymentSchedule={paymentSchedule}
        {...rest}
      />
    </Elements>
  );
};

interface ReviewConnectorWithStripeProps extends SubscribeStepProps {
  totalPrice: string;
  coupon: CouponProps;
}

const ReviewConnectorWithStripe = ({
  isElite,
  account,
  plans,
  paymentSchedule,
  onBackToPreviousStep,
  onPaymentScheduleChange,
  coupon,
  totalPrice,
  onCancel,
}: ReviewConnectorWithStripeProps) => {
  const {
    stripe,
    elements,
    isSubscribeDisabled: isSubDisabled,
    ...rest
  } = useMarketDataReviewController({
    paymentSchedule,
    plans,
    account,
    isElite,
    appliedCoupon: coupon.appliedCoupon,
    onFinished: onCancel,
  });

  const isSubscribeDisabled = isSubDisabled || coupon.isLoading;

  if (!stripe || !elements) {
    return <FullScreenSpinner />;
  }

  return (
    <ReviewPres
      isElite={isElite}
      paymentSchedule={paymentSchedule}
      onPaymentScheduleChange={onPaymentScheduleChange}
      onBackClicked={onBackToPreviousStep}
      plans={plans}
      totalPrice={totalPrice}
      coupon={coupon}
      isSubscribeDisabled={isSubscribeDisabled}
      {...rest}
    />
  );
};
